import { motion } from "framer-motion";
import { forwardRef, useState } from "react";
import { useProductData } from "@/zustand/productStore.js";
import { useCartData } from "@/zustand/cartStore.js";
import { useTemplateStore } from "@/zustand/templateStore.js";
import { useReducedProductsPrice } from "@/func/productCardHelpers.js";
import LazyLoad from "react-lazyload";
import ProductSelectorButton from "@/components/ProductPackages/ProductSelectorButton";
import { formatTextWithBreak } from "@/utils/helpers";
import s from "./ProductPackages.module.scss";
import cn from "@/utils/classNames.js";
import parse from "html-react-parser";

const ProductPackageItemV2 = forwardRef((props, ref) => {
  const productData = useProductData();
  const base = window.post_template;
  const { discountCode } = useCartData();
  const { product, handleChange, active, index } = props;
  const useProductPickerV2Fields = productData?.show_product_picker_v2;
  const isBestSeller = product.best_seller;
  const isBestValue = product.best_value;
  const discountSaveLabel = product.discount_save_label;
  const discountOffLabel = product.discount_off_label;
  const productsContent = window.post_template?.checkout_v6_products;
  const outlineBorder = productsContent.checkout_v6_products_imageWrap;
  const showBulletPoints = base?.checkout_v6_summary.checkout_v6_summary_bullet_points ?? false;

  const bestSellerLabel = window?.post_template?.checkout_v6_products?.tr_checkout_v6_products_bestSellerLabel;
  const bestValueLabel = window?.post_template?.checkout_v6_products?.tr_checkout_v6_products_bestValueLabel;
  const productSloganLabel = window?.post_template?.checkout_v6_products?.tr_checkout_v6_products_slogan_label;
  const useProductSloganLabel = productSloganLabel !== "";

  let bestValueContent = null;
  let bestSellerContent = null;
  if (isBestValue && product?.tr_best_value_status) {
    // old from product pack
    bestValueContent = formatTextWithBreak(product.tr_best_value_status?.toUpperCase());
  } else if (bestValueLabel) {
    // new approach from template
    bestValueContent = formatTextWithBreak(bestValueLabel?.toUpperCase());
  }
  if (isBestSeller && product?.status) {
    // old from product pack
    bestSellerContent = formatTextWithBreak(product.status?.toUpperCase());
  } else if (bestSellerLabel) {
    // new approach from template
    bestSellerContent = formatTextWithBreak(bestSellerLabel?.toUpperCase());
  }

  const productInfo = useReducedProductsPrice(product, productData?.currency_code, productData?.formatted_string);
  const { priceObject, promoPriceObject } = productInfo;

  const productPriceObject =
    productData?.enable_discount_codes && discountCode !== undefined ? promoPriceObject : priceObject;
  const discountPercent =
    productData.enable_discount_codes && discountCode !== undefined
      ? product.promo_discount_amount_percent
      : product.you_save_percentage;

  const fallbackImages = window.post_template.checkout_v6_products.checkout_v6_product_images;
  const matchingImage =
    fallbackImages && fallbackImages.find((obj) => obj.product_amount === product.amount)?.product_image;
  const image = matchingImage || product.image.url;

  const isEnhancedContrast = useTemplateStore((state) => state.enhancedContrast);

  return (
    <div
      data-testid="product-container"
      onClick={() => {
        handleChange(index);
      }}
      id={`productCard-${product.amount}`}
      className={cn(
        s.productPackages__container,
        s["productPackages__container--visible"],
        active && s["productPackages__container--visible"],
        !active && s["productPackages__container--unchecked"],
        isEnhancedContrast && "enhancedContrast"
      )}
    >
      <div className={s.productPackItem__firstBlock}>
        {showBulletPoints !== undefined && showBulletPoints !== null && !showBulletPoints && <ProductSelectorButton />}
        <LazyLoad offset={100}>
          <motion.div
            key={image}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              delay: 1,
              default: { duration: 0.5 },
            }}
          >
            <img
              className={`${s.productPackItem__image} mon-img-85-w mon-img-85-h ${
                outlineBorder ? s["productPackItem__image--border"] : ""
              }`}
              src={image}
              alt={product.image.alt}
            />
          </motion.div>
        </LazyLoad>
        <div className={s["productPackItem__label--container"]}>
          {isBestSeller && <div className={s["productPackItem__priceBlock--labelTag"]}>{bestSellerContent}</div>}
          {isBestValue && <div className={s["productPackItem__priceBlock--bestValueTag"]}>{bestValueContent}</div>}
          <span data-testid="product-amount" className={s["productPackItem__label--amount"]}>
            {product.amount}x {product.display_name}
          </span>
          {((useProductPickerV2Fields && product?.tr_bundle_products !== "") || useProductSloganLabel) && (
            <div
              data-testid="product-bundle-items"
              className={cn(s["productPackItem__label--bundleItems"], isEnhancedContrast && "enhancedContrast")}
            >
              <div className={s.productPackItem__slogan}>
                {useProductSloganLabel ? productSloganLabel : parse(product?.tr_bundle_products)}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={s["productPackItem__priceBlock--container"]}>
        <div data-testid="product-price-old" className={s["productPackItem__priceBlock--oldPrice"]}>
          {productInfo.oldPrice}
        </div>
        <div className={s["productPackItem__priceBlock--mainPriceBlock"]}>
          <div data-testid="product-price" className={s["productPackItem__priceBlock--price"]}>
            {productPriceObject?.symbolLeft ? (
              <>
                <sup className={s["productPackItem__priceBlock--symbol"]}>{productPriceObject?.symbol}</sup>
                {productPriceObject?.number}
                {productPriceObject?.hasCents && `.${productPriceObject?.cents}`}
              </>
            ) : (
              <>
                {productPriceObject?.number}
                {productPriceObject?.hasCents && `.${productPriceObject?.cents}`}
                <sup className={s["productPackItem__priceBlock--symbol"]}>{productPriceObject?.symbol}</sup>
              </>
            )}
          </div>
          <div className={s["productPackItem__priceBlock--centsBlock"]}>
            <div
              className={`${s["productPackItem__priceBlock--each"]} ${
                productPriceObject?.hasCents
                  ? `productPackItem__priceBlock__each--with-cents`
                  : `productPackItem__priceBlock__each--without-cents`
              } `}
            >
              {`/`}
              {window.post_template.checkout_v6_products.tr_checkout_v6_products_eachLabel}
            </div>
          </div>
        </div>
        <div className={s.productPackItem__discountPercent}>
          <p>
            {discountSaveLabel} {discountPercent}% {discountOffLabel}
          </p>
        </div>
      </div>
    </div>
  );
});

export { ProductPackageItemV2 };
